import { keyframes } from '@emotion/react';
import { Box, Typography } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';

// ═══════════════════════════════════════════════════════════════════════
//  Types
// ═══════════════════════════════════════════════════════════════════════

interface LoaderContent {
  emoji: string;
  heading: string;
  subHeading: string;
  messages: string[];
}

export interface PalLoaderProps extends LoaderContent {
  contentLockKey: string;
  isLoaderLoaded: boolean;
  setIsLoaderLoaded: (v: boolean) => void;
  /** When set, loader marks itself loaded after this many ms (message steps scale to fit). */
  loadedAfterMs?: number;
}

// ═══════════════════════════════════════════════════════════════════════
//  Hooks
// ═══════════════════════════════════════════════════════════════════════

/**
 * Holds a snapshot of content props.  Updates only when `lockKey` changes
 * AND the loader is idle.  If the key changes mid-animation, the new
 * content is queued and applied once `isLoaderLoaded` becomes true.
 */
function useDeferredContent(
  incoming: LoaderContent,
  lockKey: string,
  isLoaderLoaded: boolean
): LoaderContent {
  const [locked, setLocked] = useState(incoming);
  const prevKeyRef = useRef(lockKey);
  const loadedRef = useRef(isLoaderLoaded);
  loadedRef.current = isLoaderLoaded;
  const pendingRef = useRef<LoaderContent | null>(null);

  useEffect(() => {
    if (prevKeyRef.current !== lockKey) {
      prevKeyRef.current = lockKey;
      if (loadedRef.current) {
        setLocked(incoming);
        pendingRef.current = null;
      } else {
        pendingRef.current = incoming;
      }
    }
  }, [
    lockKey,
    incoming.emoji,
    incoming.heading,
    incoming.subHeading,
    incoming.messages.join(','),
  ]);

  useEffect(() => {
    if (isLoaderLoaded && pendingRef.current) {
      setLocked(pendingRef.current);
      pendingRef.current = null;
    }
  }, [isLoaderLoaded]);

  return locked;
}

const STEP_MS = 750;

/**
 * Steps through `messages` one at a time, advancing every STEP_MS
 * (or scaled so the full sequence fits `loadedAfterMs` when provided).
 * Calls `onStart()` at the beginning and `onDone()` once every message
 * has displayed for a full step.  Both callbacks live in the same effect
 * so reset + stepper always fire together.
 */
function useMessageStepper(
  messages: string[],
  onStart: () => void,
  onDone: () => void,
  loadedAfterMs?: number
) {
  const safe = useMemo(
    () => messages.filter((m) => m.trim().length > 0),
    [messages]
  );
  const [index, setIndex] = useState(0);

  const contentKey = safe.join(',');

  useEffect(() => {
    onStart();
    setIndex(0);
    const n = safe.length;
    if (n === 0) {
      if (loadedAfterMs != null && loadedAfterMs > 0) {
        const doneId = window.setTimeout(onDone, loadedAfterMs);
        return () => window.clearTimeout(doneId);
      }
      onDone();
      return;
    }

    const totalMs = loadedAfterMs ?? (n + 1) * STEP_MS;
    const stepMs = totalMs / (n + 1);
    const doneId = window.setTimeout(onDone, totalMs);

    let tickId: number | undefined;
    if (n > 1) {
      tickId = window.setInterval(() => {
        setIndex((i) => {
          if (i >= n - 1) {
            window.clearInterval(tickId);
            return i;
          }
          return i + 1;
        });
      }, stepMs);
    }

    return () => {
      window.clearTimeout(doneId);
      if (tickId !== undefined) window.clearInterval(tickId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentKey, loadedAfterMs]);

  const total = safe.length;
  return {
    statusText: total === 0 ? '' : safe[index] ?? '',
    progressPercent:
      total === 0 ? 0 : Math.min(100, ((index + 1) / total) * 100),
    showBar: total > 1,
  };
}

// ═══════════════════════════════════════════════════════════════════════
//  Keyframes
// ═══════════════════════════════════════════════════════════════════════

const alPulse = keyframes`
  0%   { transform: scale(0.6); opacity: 0; border-width: 4px; }
  20%  { opacity: 0.8; }
  100% { transform: scale(1.4); opacity: 0; border-width: 1px; }
`;

const alBrainFloat = keyframes`
  0%, 100% { transform: translateY(0); }
  50%      { transform: translateY(-4px); }
`;

const alDotBlink = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50%      { opacity: 0.4; transform: scale(0.85); }
`;

// ═══════════════════════════════════════════════════════════════════════
//  Static Styles
// ═══════════════════════════════════════════════════════════════════════

const backdropSx = {
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  minHeight: '100%',
  alignSelf: 'stretch',
  flex: '1 1 auto',
  boxSizing: 'border-box',
  background: 'rgba(35, 22, 70, 0.7)',
  backdropFilter: 'blur(40px) saturate(160%)',
  WebkitBackdropFilter: 'blur(40px) saturate(160%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  p: '20px',
  zIndex: 2,
} as const;

const modalSx = {
  background: 'white',
  borderRadius: '28px',
  px: '44px',
  pt: '48px',
  pb: '40px',
  maxWidth: 480,
  width: '100%',
  textAlign: 'center',
  boxShadow: '0 30px 80px rgba(0, 0, 0, 0.3)',
} as const;

const graphicSx = {
  position: 'relative',
  width: 120,
  height: 120,
  mx: 'auto',
  mb: '28px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
} as const;

const GRADIENT = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';

const iconSx = {
  position: 'relative',
  width: 88,
  height: 88,
  background: GRADIENT,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 42,
  boxShadow: '0 8px 24px rgba(102, 126, 234, 0.4)',
  animation: `${alBrainFloat} 2s ease-in-out infinite`,
} as const;

const statusPillSx = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  py: '7px',
  px: '16px',
  background: '#eef2ff',
  borderRadius: '20px',
  mb: '20px',
} as const;

const titleSx = {
  fontSize: 26,
  fontWeight: 800,
  margin: '0 0 8px',
  lineHeight: 1.25,
  color: '#667eea',
} as const;

const subtitleSx = {
  fontSize: 14,
  color: '#64748b',
  margin: '0 0 28px',
  fontWeight: 500,
  lineHeight: 1.5,
} as const;

// ═══════════════════════════════════════════════════════════════════════
//  Component
// ═══════════════════════════════════════════════════════════════════════

export const PalLoader = ({
  contentLockKey,
  emoji = '🧠',
  heading,
  subHeading,
  messages,
  isLoaderLoaded,
  setIsLoaderLoaded,
  loadedAfterMs,
}: PalLoaderProps) => {
  const content = useDeferredContent(
    { emoji, heading, subHeading, messages },
    contentLockKey,
    isLoaderLoaded
  );

  const { statusText, progressPercent, showBar } = useMessageStepper(
    content.messages,
    () => setIsLoaderLoaded(false),
    () => setIsLoaderLoaded(true),
    loadedAfterMs
  );

  return (
    <Box id="palLoaderPage" sx={backdropSx}>
      <Box sx={modalSx}>
        {/* Pulsing graphic */}
        <Box sx={graphicSx}>
          {[0, 1, 2].map((ring) => (
            <Box
              key={ring}
              sx={{
                position: 'absolute',
                inset: 0,
                borderRadius: '50%',
                border: '3px solid #667eea',
                opacity: 0,
                animation: `${alPulse} 2.4s ease-out infinite`,
                animationDelay: `${ring * 0.8}s`,
              }}
            />
          ))}
          <Box sx={iconSx}>{content.emoji}</Box>
        </Box>

        {/* Status pill */}
        <Box sx={statusPillSx}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: '#667eea',
              animation: `${alDotBlink} 1.4s ease-in-out infinite`,
            }}
          />
          <Typography
            component="span"
            sx={{
              fontSize: 11,
              fontWeight: 700,
              color: '#4f46e5',
              textTransform: 'uppercase',
              letterSpacing: '0.4px',
              whiteSpace: 'nowrap',
            }}
          >
            {statusText}
          </Typography>
        </Box>

        <Typography component="h2" sx={titleSx}>
          {content.heading}
        </Typography>

        <Typography component="p" sx={subtitleSx}>
          {content.subHeading}
        </Typography>

        {showBar && (
          <Box
            sx={{
              height: 8,
              background: '#eef2ff',
              borderRadius: '8px',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                height: '100%',
                width: `${progressPercent}%`,
                background: 'linear-gradient(90deg, #667eea, #764ba2)',
                borderRadius: '6px',
                transition: 'width 0.5s ease',
              }}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default PalLoader;
